import { useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { AdminSidebar } from '@/layouts/AdminSidebar';
import { DesktopNavbar, MobileHeader } from '@/layouts/TopNavbar';
import { useCurrentUser } from '@/hooks/useAuth';
import { useNavigationState } from '@/layouts/useNavigationState';
import { useLocation } from '@tanstack/react-router';

/** Route-level layout: sidebar + navbar. Pages render into `children` via the router Outlet. */
export function AdminShell({ children }: { children: ReactNode }) {
  const { data: user } = useCurrentUser();
  const moduleType = user?.module_type ?? user?.moduleType ?? 'food';
  const location = useLocation();
  const nav = useNavigationState();
  const sidebarRef = useRef<HTMLElement>(null);
  const wasMobileOpen = useRef(false);

  // Escape: close dropdown first, then drawer.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (nav.moduleMenuOpen) {
        nav.setModuleMenuOpen(false);
        return;
      }
      if (nav.isMobile && nav.mobileOpen) {
        nav.setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [nav]);

  // Focus management for the mobile drawer.
  useEffect(() => {
    if (nav.isMobile && nav.mobileOpen && !wasMobileOpen.current) {
      requestAnimationFrame(() => {
        sidebarRef.current?.querySelector<HTMLElement>('[data-sidebar-close]')?.focus();
      });
    }
    wasMobileOpen.current = nav.mobileOpen;
  }, [nav.isMobile, nav.mobileOpen]);

  // Any navigation closes the mobile drawer + module menu.
  useEffect(() => {
    if (nav.isMobile) {
      nav.setMobileOpen(false);
      nav.setModuleMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.href]);

  const closeMobile = useCallback(() => nav.setMobileOpen(false), [nav]);
  const closeModuleMenu = useCallback(() => nav.setModuleMenuOpen(false), [nav]);
  const effectiveCollapsed = nav.isMobile ? false : nav.collapsed;

  return (
    <div
      className={[
        'admin-shell',
        effectiveCollapsed ? 'sidebar-collapsed' : '',
        nav.isMobile && nav.mobileOpen ? 'mobile-drawer-open' : '',
        nav.isMobile ? 'is-mobile' : 'is-desktop',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <AdminSidebar
        ref={sidebarRef}
        moduleType={String(moduleType)}
        collapsed={effectiveCollapsed}
        isMobile={nav.isMobile}
        mobileOpen={nav.mobileOpen}
        searchFocusNonce={nav.searchFocusNonce}
        expandedGroups={nav.expandedGroups}
        onToggleCollapsed={nav.toggleCollapsed}
        onToggleGroup={nav.toggleGroup}
        onEnsureGroupOpen={nav.ensureGroupOpen}
        onSetAllGroups={nav.setAllGroups}
        onNavigate={closeMobile}
        onCloseMobile={closeMobile}
        onOpenSearch={nav.openNavForSearch}
      />

      {nav.isMobile && nav.mobileOpen ? (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close navigation"
          onClick={closeMobile}
        />
      ) : null}

      <div className="admin-main">
        {nav.isMobile ? (
          <MobileHeader
            onOpenDrawer={() => nav.setMobileOpen(true)}
            onOpenSearch={nav.openNavForSearch}
            moduleMenuOpen={nav.moduleMenuOpen}
            onToggleModuleMenu={() => nav.setModuleMenuOpen(!nav.moduleMenuOpen)}
            onCloseModuleMenu={closeModuleMenu}
          />
        ) : (
          <DesktopNavbar onToggleSidebar={nav.toggleCollapsed} onOpenSearch={nav.openNavForSearch} />
        )}

        <main className="admin-content">{children}</main>
      </div>
    </div>
  );
}
